// src/contexts/EffortContext.tsx
import React, { createContext, useCallback, useContext, useState } from 'react';
import { EffortsList, SupporterToEffortList } from '../types/efforts';

export const EFFORT_DETAILS_TAB_IDS = [
  'overview',
  'activity',
  'results',
  'stories',
  'legacy-challenge',
  'assets',
] as const;

export type EffortDetailsTabId = (typeof EFFORT_DETAILS_TAB_IDS)[number];

interface EffortContextType {
  effortId: string | null;
  effortName: string | null;
  effortReasons: string | null;
  setEffortId: (id: string) => void;
  setEffortName: (name: string) => void;
  setEffortReasons: (reasons: string | null) => void;
  allSuppToEfforts: SupporterToEffortList | null;
  setAllSuppToEfforts: React.Dispatch<
    React.SetStateAction<SupporterToEffortList | null>
  >;
  orgEfforts: EffortsList | null;
  setOrgEfforts: React.Dispatch<React.SetStateAction<EffortsList | null>>;
  orgEffortIds: string[];
  setOrgEffortIds: React.Dispatch<React.SetStateAction<string[]>>;
  selEffortStatus: string;
  setSelEffortStatus: React.Dispatch<React.SetStateAction<string>>;
  selectedEmail: SelectedEmail | null;
  setSelectedEmail: React.Dispatch<React.SetStateAction<SelectedEmail | null>>;
  selectedFormId: string | null;
  setSelectedFormId: (id: string) => void;
  selectedFormName: string | null;
  setSelectedFormName: (name: string) => void;
  isSelEffortPurchased: boolean;
  setIsSelEffortPurchased: React.Dispatch<React.SetStateAction<boolean>>;
  /** One-shot effort-details tab handoff (resolved by EffortNavigation). */
  effortDetailsTab: EffortDetailsTabId | null;
  setEffortDetailsTab: (tab: EffortDetailsTabId | null) => void;
  /** Bumped to reload overview deliverables / effort email templates. */
  effortEmailsRefreshKey: number;
  refreshEffortEmails: () => void;
}

export type SelectedEmail = {
  email_template_id?: string | number;
  template_name?: string;
  content?: string;
  subject?: string;
  sender_id?: string | null;
  reply_to_id?: string | null;
  is_alternate_from_name?: boolean;
  is_supporter_signer?: boolean;
  id?: string;
  delay_days?: number;
  verbiage?: string;
  kind?: string;
  chain?: string | null;
  status?: string;
};

const EffortContext = createContext<EffortContextType | undefined>(undefined);

export const EffortProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [effortId, setEffortId] = useState<string | null>(null);
  const [effortName, setEffortName] = useState<string | null>(null);
  const [effortReasons, setEffortReasons] = useState<string | null>(null);
  const [allSuppToEfforts, setAllSuppToEfforts] =
    useState<SupporterToEffortList | null>({
      results: [],
      meta: {
        total: 0,
        page: 1,
        page_size: 50,
        total_pages: 0,
        next_page: null,
        prev_page: null,
      },
    });
  const [orgEfforts, setOrgEfforts] = useState<EffortsList | null>({
    efforts: [],
  });
  const [orgEffortIds, setOrgEffortIds] = useState<string[]>([]);
  const [selEffortStatus, setSelEffortStatus] = useState('');
  const [selectedEmail, setSelectedEmail] = useState<SelectedEmail | null>(
    null
  );
  // when a form gets selected from the effort page
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [selectedFormName, setSelectedFormName] = useState<string | null>(null);
  const [isSelEffortPurchased, setIsSelEffortPurchased] =
    useState<boolean>(true);
  const [effortDetailsTab, setEffortDetailsTab] =
    useState<EffortDetailsTabId | null>(null);
  const [effortEmailsRefreshKey, setEffortEmailsRefreshKey] = useState(0);
  const refreshEffortEmails = useCallback(() => {
    setEffortEmailsRefreshKey(key => key + 1);
  }, []);

  return (
    <EffortContext.Provider
      value={{
        effortId,
        setEffortId,
        effortName,
        setEffortName,
        effortReasons,
        setEffortReasons,
        allSuppToEfforts,
        setAllSuppToEfforts,
        orgEfforts,
        setOrgEfforts,
        orgEffortIds,
        setOrgEffortIds,
        selEffortStatus,
        setSelEffortStatus,
        selectedEmail,
        setSelectedEmail,
        selectedFormId,
        setSelectedFormId,
        selectedFormName,
        setSelectedFormName,
        isSelEffortPurchased,
        setIsSelEffortPurchased,
        effortDetailsTab,
        setEffortDetailsTab,
        effortEmailsRefreshKey,
        refreshEffortEmails,
      }}
    >
      {children}
    </EffortContext.Provider>
  );
};

export const useEffort = (): EffortContextType => {
  const context = useContext(EffortContext);
  if (!context) {
    throw new Error('useEffort must be used within EffortProvider');
  }
  return context;
};
